"use client";

import { useState } from "react";
import Breadcrumb from "@/components/Breadcrumb";
import { Send, MapPin, Mail, Sparkles, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

const CRM_LEADS_API_URL = "https://crmleadsbackend.oxmite.com/api/leads";
const CRM_SITE_DOMAIN = "highlandgroup.ch";

export default function Contact({ params }) {
  const locale = normalizeLocale(params?.locale);
  const canonical = sitePageUrl(locale, "contact");
  const homeUrl = sitePageUrl(locale);
  const orgId = `${SITE_URL}/#organization`;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
   
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const page = typeof window !== "undefined" ? window.location.href : canonical;

      const res = await fetch(CRM_LEADS_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: `${CRM_SITE_DOMAIN}/contact`,
          sourceType: "contact_form",
          sourceId: "contact",
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            message: formData.message,
            page: page || `https://${CRM_SITE_DOMAIN}/contact`,
          },
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to send message. Please try again.");
      }

      setStatus("success");
      setFormData({ firstName: "", lastName: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "Something went wrong. Please try again.");
    }
  };

  const contactJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: "Highlandgroup",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
        email: "highlandgroupgmbh@gmail.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Bahnhofstrasse 21",
          postalCode: "3860",
          addressLocality: "Meiringen",
          addressCountry: "CH",
        },
      },
      {
        "@type": "ContactPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: "Contact Highlandgroup | Book AI Consultation",
        description:
          "Book AI consultation in Switzerland with Highlandgroup for automation, software development, SaaS, websites, and hotel digital solutions.",
        inLanguage: locale,
        about: { "@id": orgId },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Contact", item: canonical },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Contact" },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Highlandgroup | Book Consultation</title>
        <meta
          name="description"
          content="Book consultation in Switzerland with Highlandgroup for automation, software development, SaaS, websites, and hotel digital solutions."
        />
        <meta
          name="keywords"
          content="Book consultation Switzerland, contact software company Switzerland, hire AI automation company Switzerland"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Contact Highlandgroup" />
        <meta
          property="og:description"
          content="Speak with our team about AI automation, software development, or digital growth solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Highlandgroup" />
        <meta
          name="twitter:description"
          content="Speak with our team about AI automation, software development, or digital growth solutions."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(contactJsonLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-bg-soft text-charcoal selection:bg-accent-red/30">
        {/* Breadcrumb Section */}
        <div className="bg-gradient-to-b from-bg-soft/50 to-transparent">
          <Breadcrumb
            items={breadcrumbItems}
            title="Contact AI Automation Company in Switzerland"
            subtitle="Highland Group is an AI solutions company and software development company in Switzerland helping businesses across Europe grow through AI automation services, websites, SaaS products, and hotel digital solutions. We focus on practical solutions that deliver real business results."
            description="Turn your vision into success!"
          />
        </div>

        <section className="py-20 md:py-28 px-6" id="contact-form">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-start">

            {/* Left Side */}
            <div className="lg:col-span-5 space-y-12 order-2 lg:order-1">
              <div className="relative z-10 space-y-8 text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bg-main text-accent-red text-sm font-medium mb-2">
                  <Sparkles className="w-4 h-4" /> Available for New Projects
                </div>

                <h2 className="text-4xl md:text-6xl font-semibold text-charcoal">
                  Let&apos;s Build Your <br />
                  <span className="text-accent-red">Next Digital Solution</span>
                </h2>

                <p className="text-md text-muted max-w-md leading-relaxed">
                  Whether you need a high-performing website from a trusted web development company in Switzerland, a scalable SaaS product, or advanced AI Business automation services, Highland Group is ready to support your growth.
                </p>
              </div>

              {/* Image */}
              <div className="relative mx-auto lg:mx-0 max-w-sm">
                <div className="absolute inset-0 bg-red-200 rounded-[2rem] rotate-3 scale-105 opacity-30" />
                <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
                  <img
                    src="/assets/img-27.jpg"
                    alt="Highland Group Workspace"
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-1000"
                  />
                </div>
              </div>
            </div>

            {/* Right Side Form */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="bg-bg-main p-8 md:p-12 rounded-[2.5rem] shadow-[0_20px_50px_rgba(217,31,38,0.15)] border border-border-light relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-bg-soft rounded-full -mr-16 -mt-16 blur-3xl" />

                <form className="relative z-10 space-y-6" onSubmit={handleSubmit}>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-accent-red ml-1">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:border-accent-red focus:ring-4 focus:ring-accent-red/20 outline-none transition-all"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-accent-red ml-1">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:border-accent-red focus:ring-4 focus:ring-accent-red/20 outline-none transition-all"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-accent-red ml-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:border-accent-red focus:ring-4 focus:ring-accent-red/20 outline-none transition-all"
                      placeholder="info@highlandgroup.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-accent-red ml-1">Your Message</label>
                    <textarea
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-6 py-4 bg-bg-soft rounded-2xl focus:border-accent-red focus:ring-4 focus:ring-accent-red/20 outline-none transition-all resize-none"
                      placeholder="Tell us about your project..."
                      required
                    />
                  </div>

                  {status === "success" && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-50 text-green-700 text-sm font-medium">
                      <CheckCircle2 className="w-5 h-5 shrink-0" />
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}

                  {status === "error" && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium">
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full py-5 bg-accent-red hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold text-lg rounded-2xl shadow-lg shadow-accent-red/30 transition-all flex items-center justify-center gap-3"
                  >
                    {status === "loading" ? (
                      <>
                        Sending...
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </>
                    ) : (
                      <>
                        Send Message 
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-8 px-6 pb-20">
          {/* Location / Address */}
          <div className="flex items-center gap-4 p-6 h-36 rounded-2xl bg-bg-main shadow-md border border-border-light">
            <div className="w-14 h-14 flex items-center justify-center bg-accent-red/20 rounded-xl text-accent-red">
              <MapPin className="w-7 h-7" />
            </div>
            <div>
              <p className="text-xs text-charcoal font-bold uppercase">Location</p>
              <p className="text-sm font-medium text-muted">
                <a
                  href="https://maps.app.goo.gl/wC5QgkJp5MQJ7e8M9"
                  target="_blank"
                  className="hover:underline"
                >
                  Bahnhofstrasse 21, 3860 Meiringen, Switzerland
                </a>
              </p>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center gap-4 p-6 h-36 rounded-2xl bg-bg-main shadow-md border border-border-light">
            <div className="w-14 h-14 flex items-center justify-center bg-accent-red/20 text-accent-red rounded-xl">
              <Mail className="w-7 h-7" />
            </div>
            <div>
              <p className="text-xs text-accent-red font-bold uppercase">Email Us</p>
              <p className="text-sm font-medium">
                <a href="mailto:highlandgroupgmbh@gmail.com" className="hover:underline">
                  highlandgroupgmbh@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Phone / WhatsApp */}
          {/* <div className="flex items-center gap-4 p-6 h-36 rounded-2xl bg-bg-main shadow-md border border-border-light">
            <div className="w-14 h-14 flex items-center justify-center bg-accent-red/20 text-accent-red rounded-xl">
              <Phone className="w-7 h-7" />
            </div>
            <div>
              <p className="text-xs text-accent-red font-bold uppercase">Call / WhatsApp</p>
              <p className="text-sm font-medium">
                <a href="tel:+4915223506691" className="hover:underline">+49 1522 3506691</a> |{" "}
                <a href="https://wa.me/4915223506691" target="_blank" className="hover:underline">
                  WhatsApp
                </a>
              </p>
            </div>
          </div> */}
        </section>
      </main>
    </>
  );
}
